import React, { useState, useEffect, useCallback, useRef } from 'react';
import { safeSessionStorage } from '../../../../utils/safeStorage';
import { GuidedPreflight } from './GuidedPreflight';
import { GuidedFacecam } from './GuidedFacecam';
import { GuidedCountdown } from './GuidedCountdown';
import { GuidedLaunched } from './GuidedLaunched';
import { GuidedUpload } from './GuidedUpload';
import { safeExitPiP } from '../../../../utils/mediaUtils';
import './GuidedCapture.css';

export type GuidedStep = 'preflight' | 'facecam' | 'countdown' | 'launched' | 'upload';

export interface GuidedCaptureProps {
  taskName: string;
  prompt: string;
  /** Optional deep-link URL to launch the target app */
  targetUrl?: string;
  /** Task ID for sessionStorage keying */
  taskId?: string;
  onSubmit: (file: File, notes: string) => void;
  onSaveDraft: (file: File | null, notes: string) => void;
  onClose: () => void;
  initialDraft?: { notes: string; mediaBlobs: Blob[] };
}

const isAndroid = typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent);

const getStorageKey = (taskId?: string) =>
  taskId ? `guidedScreenPhase_${taskId}` : 'guidedScreenPhase';

export const GuidedCapture: React.FC<GuidedCaptureProps> = ({
  taskName,
  targetUrl,
  taskId,
  onSubmit,
  onSaveDraft,
  onClose,
  initialDraft,
}) => {
  // Face camera stream lives here (parent), persists across child step changes.
  // Matching legacy: faceStreamRef stays alive until cleanup/close.
  const faceStreamRef = useRef<MediaStream | null>(null);

  const hasDraftMedia = !!initialDraft?.mediaBlobs.length;

  const [step, setStep] = useState<GuidedStep>(() => {
    if (hasDraftMedia) return 'upload';
    if (typeof window !== 'undefined') {
      const storageKey = getStorageKey(taskId);
      const savedPhase = safeSessionStorage.getItem(storageKey);
      if (savedPhase === 'upload' || window.location.hash === '#guided-upload') {
        return 'upload';
      }
      if (savedPhase === 'launched') {
        return 'launched';
      }
    }
    return 'preflight';
  });

  // Persist step changes to sessionStorage
  useEffect(() => {
    if (step === 'launched' || step === 'upload') {
      const storageKey = getStorageKey(taskId);
      safeSessionStorage.setItem(storageKey, step);
    }
  }, [step, taskId]);

  // Safety net: exit PiP whenever entering upload step
  useEffect(() => {
    if (step === 'upload') {
      safeExitPiP();
    }
  }, [step]);

  // Stop face camera stream on unmount (matching legacy line 714-720)
  useEffect(() => {
    return () => {
      if (faceStreamRef.current) {
        faceStreamRef.current.getTracks().forEach(t => t.stop());
      }
    };
  }, []);

  const cleanup = useCallback(() => {
    const storageKey = getStorageKey(taskId);
    safeSessionStorage.removeItem(storageKey);
    if (window.location.hash === '#guided-upload') {
      window.location.hash = '';
    }
  }, [taskId]);

  // Deep-link navigation with window.location.href + 2.5s fallback
  const handleCountdownComplete = useCallback(() => {
    const storageKey = getStorageKey(taskId);
    safeSessionStorage.setItem(storageKey, 'launched');

    if (targetUrl) {
      const launchTime = Date.now();
      window.location.href = targetUrl;

      // Fallback: if browser still visible after 2.5s, app likely didn't open
      setTimeout(() => {
        if (document.visibilityState === 'visible' && Date.now() - launchTime < 4000) {
          // App didn't launch — stay on launched step
        }
        setStep('launched');
      }, 2500);
    } else {
      setStep('launched');
    }
  }, [targetUrl, taskId]);

  // Full cleanup on close: exit PiP, stop face stream, clear storage
  const handleClose = () => {
    safeExitPiP();
    if (faceStreamRef.current) {
      faceStreamRef.current.getTracks().forEach(t => t.stop());
      faceStreamRef.current = null;
    }
    cleanup();
    onClose();
  };

  const handleUploadReady = useCallback(() => {
    const storageKey = getStorageKey(taskId);
    safeSessionStorage.setItem(storageKey, 'upload');
    window.location.hash = 'guided-upload';
    setStep('upload');
  }, [taskId]);

  switch (step) {
    case 'preflight':
      return (
        <GuidedPreflight
          taskName={taskName}
          onNext={() => setStep('facecam')}
          onSkipToUpload={() => setStep('upload')}
          onClose={handleClose}
        />
      );

    case 'facecam':
      return (
        <GuidedFacecam
          taskName={taskName}
          isAndroid={isAndroid}
          faceStreamRef={faceStreamRef}
          onNext={() => setStep('countdown')}
          onClose={handleClose}
        />
      );

    case 'countdown':
      return (
        <GuidedCountdown
          taskName={taskName}
          onComplete={handleCountdownComplete}
          onClose={handleClose}
        />
      );

    case 'launched':
      return (
        <GuidedLaunched
          taskName={taskName}
          onUpload={handleUploadReady}
          onClose={handleClose}
        />
      );

    case 'upload': {
      const draftFile = initialDraft?.mediaBlobs.length
        ? new File([initialDraft.mediaBlobs[0]], 'draft-recording', { type: initialDraft.mediaBlobs[0].type || 'video/mp4' })
        : undefined;
      return (
        <GuidedUpload
          taskName={taskName}
          onSubmit={onSubmit}
          onSaveDraft={onSaveDraft}
          onClose={handleClose}
          onCleanup={cleanup}
          initialFile={draftFile}
          initialNotes={initialDraft?.notes}
        />
      );
    }

    default:
      return null;
  }
};
